import React from 'react';
import { motion } from 'framer-motion';
import { Clock, Users, TrendingUp, Shield } from 'lucide-react';

const Benefits = () => {
  const benefits = [
    {
      icon: Clock,
      title: 'Save 80% Production Time',
      description: 'Automated graphics generation means no more manual overlay creation. Focus on content, not technical setup.',
      stats: '80% faster than traditional methods'
    },
    {
      icon: Users,
      title: 'Boost Viewer Engagement',
      description: 'Professional graphics keep viewers engaged 3x longer with dynamic, informative overlays.',
      stats: '300% increase in viewer retention'
    },
    {
      icon: TrendingUp,
      title: 'Increase Revenue Streams',
      description: 'Professional-quality broadcasts attract sponsors and premium advertising opportunities.',
      stats: 'Average 150% revenue increase'
    },
    {
      icon: Shield,
      title: 'Zero Technical Expertise Required',
      description: 'Intuitive interface means anyone can create broadcast-quality sports graphics in minutes.',
      stats: 'Setup in under 5 minutes'
    }
  ];

  return (
    <section className="py-20 relative">
      <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-blue-900/20 to-slate-900"></div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Why Broadcasters Choose
            <span className="block bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
              Sport Stream Kit
            </span>
          </h2>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Join thousands of broadcasters who've revolutionized their sports coverage with our professional graphics platform.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Benefits List */}
          <motion.div 
            className="space-y-8"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                className="flex items-start space-x-4 group"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="flex-shrink-0 p-3 bg-gradient-to-r from-blue-500 to-cyan-400 rounded-xl group-hover:scale-110 transition-transform duration-300">
                  <benefit.icon className="h-6 w-6 text-white" />
                </div>
                <div className="flex-grow">
                  <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-blue-400 transition-colors">
                    {benefit.title}
                  </h3>
                  <p className="text-slate-300 mb-2 leading-relaxed">
                    {benefit.description}
                  </p>
                  <div className="text-sm font-medium text-cyan-400">
                    {benefit.stats}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Image */}
          <motion.div 
            className="relative"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="relative rounded-2xl overflow-hidden">
              <img 
                src="https://images.pexels.com/photos/1714208/pexels-photo-1714208.jpeg" 
                alt="Graphics software interface"
                className="w-full h-96 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent"></div>
            </div>
            
            {/* Floating Stats Cards */}
            <motion.div 
              className="absolute -top-4 -right-4 bg-slate-800/90 backdrop-blur-sm border border-slate-600 rounded-xl p-4"
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 3, repeat: Infinity }}
            >
              <div className="text-2xl font-bold text-white">10,000+</div>
              <div className="text-sm text-slate-300">Happy Users</div>
            </motion.div>

            <motion.div 
              className="absolute -bottom-4 -left-4 bg-slate-800/90 backdrop-blur-sm border border-slate-600 rounded-xl p-4"
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 3, repeat: Infinity, delay: 1.5 }}
            >
              <div className="text-2xl font-bold text-blue-400">99.9%</div>
              <div className="text-sm text-slate-300">Uptime</div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Benefits;